/**
 * Timezone arithmetic for the daily report.
 *
 * "End of day" is a local-business concept, but everything is stored in UTC, so
 * these helpers translate between the two. They're built on `Intl` rather than a
 * date library so there is no dependency to drift out of date with tzdata, and
 * they handle DST transitions — the day a report would otherwise cover 23 or 25
 * hours, or fire twice.
 */

export interface LocalParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number;
  second: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

/** Wall-clock time in `timeZone` at the given instant. */
export function toLocalParts(instant: Date, timeZone: string): LocalParts {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const lookup: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== "literal") lookup[part.type] = part.value;
  }
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    // Intl can emit "24" for midnight under some engines/locales.
    hour: Number(lookup.hour) % 24,
    minute: Number(lookup.minute),
    second: Number(lookup.second),
  };
}

/** Zone offset in ms at a given instant (positive east of UTC). */
function offsetMsAt(instant: Date, timeZone: string): number {
  const parts = toLocalParts(instant, timeZone);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  // Intl truncates to whole seconds; keep the sub-second remainder out of the offset.
  return asUtc - (instant.getTime() - instant.getMilliseconds());
}

/**
 * The UTC instant at which the given local wall-clock time occurs.
 *
 * Resolved in two passes: the first guess uses the offset at the naive instant,
 * the second corrects it using the offset actually in force at that moment.
 * That second pass is what makes DST transitions come out right.
 */
export function zonedTimeToUtc(parts: Omit<LocalParts, "second"> & { second?: number }, timeZone: string): Date {
  const naive = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second ?? 0,
  );

  let result = naive - offsetMsAt(new Date(naive), timeZone);
  result = naive - offsetMsAt(new Date(result), timeZone);
  return new Date(result);
}

/** `YYYY-MM-DD` for the local calendar day containing `instant`. */
export function localDateKey(instant: Date, timeZone: string): string {
  const { year, month, day } = toLocalParts(instant, timeZone);
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Parse a `YYYY-MM-DD` key. Returns null when malformed. */
export function parseDateKey(dateKey: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;
  const [, y, m, d] = match;
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/**
 * UTC half-open interval `[start, end)` covering one local calendar day.
 * On DST days this is correctly 23 or 25 hours long.
 */
export function dayBoundsUtc(dateKey: string, timeZone: string): { start: Date; end: Date } {
  const parsed = parseDateKey(dateKey);
  if (!parsed) throw new Error(`Invalid date key "${dateKey}" (expected YYYY-MM-DD)`);

  const start = zonedTimeToUtc({ ...parsed, hour: 0, minute: 0 }, timeZone);
  // Advance by calendar day rather than +24h so DST can't shift the boundary.
  const nextDay = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day + 1));
  const end = zonedTimeToUtc(
    {
      year: nextDay.getUTCFullYear(),
      month: nextDay.getUTCMonth() + 1,
      day: nextDay.getUTCDate(),
      hour: 0,
      minute: 0,
    },
    timeZone,
  );

  return { start, end };
}

/** The local instant at which a given day's report becomes due. */
export function reportDueAt(
  dateKey: string,
  timeZone: string,
  hour: number,
  minute: number,
): Date {
  const parsed = parseDateKey(dateKey);
  if (!parsed) throw new Error(`Invalid date key "${dateKey}" (expected YYYY-MM-DD)`);
  return zonedTimeToUtc({ ...parsed, hour, minute }, timeZone);
}

/** Previous local calendar day, as a date key. */
export function previousDateKey(dateKey: string): string {
  const parsed = parseDateKey(dateKey);
  if (!parsed) throw new Error(`Invalid date key "${dateKey}" (expected YYYY-MM-DD)`);
  const previous = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day - 1));
  return `${previous.getUTCFullYear()}-${pad(previous.getUTCMonth() + 1)}-${pad(previous.getUTCDate())}`;
}

/**
 * Human-friendly local timestamp for emails, e.g. "Aug 14, 2026, 2:05 PM EDT".
 *
 * Uses explicit component options rather than dateStyle/timeStyle: ECMA-402
 * forbids combining those shorthands with `timeZoneName`, and doing so throws
 * at runtime rather than degrading.
 */
export function formatLocal(instant: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(instant);
}

/** Long-form local date for report headings, e.g. "Friday, August 14, 2026". */
export function formatDateKeyLong(dateKey: string, timeZone: string): string {
  const parsed = parseDateKey(dateKey);
  if (!parsed) return dateKey;
  // Noon avoids any chance of a DST edge shifting the rendered day.
  const noon = zonedTimeToUtc({ ...parsed, hour: 12, minute: 0 }, timeZone);
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(noon);
}

/** PocketBase filter format: "YYYY-MM-DD HH:mm:ss.SSSZ" */
export function toPocketBaseDate(instant: Date): string {
  return instant.toISOString().replace("T", " ");
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}
